import React from "react";
import styled, { css } from "styled-components";
import { format } from "date-fns";
import DatePicker from "../../components/DatePicker";
import { BalanceLogContext } from "./ProvideBalanceLog";
import { narrowScreenMediaQuery, orangish } from "../../constants/styles";
import { ModalContext } from "../../providers/ProvideModal";
import Section, { SectionTitle } from "../../commons/Section";
import { unmodule } from "../../util";

// Helper function to determine group type from translated text
const getGroupType = (groupText) => {
  if (!groupText) return null;
  const lowerText = groupText.toLowerCase();
  if (lowerText.includes("cash in [accepted]")) return "in_accepted";
  if (lowerText.includes("cash out [accepted]")) return "out_accepted";
  if (lowerText.includes("cash in [pending]")) return "in_pending";
  if (lowerText.includes("cash out [pending]")) return "out_pending";
  if (lowerText.includes("cash in [refunded]")) return "in_refunded";
  return null;
};

// Helper function to determine if it's cash in or cash out
const isCashIn = (groupType) => {
  return groupType && groupType.startsWith("in_");
};

// BalanceLog Detail Modal Component
const BalanceLogDetailModal = ({ data }) => {
  // Null checks and fallback values
  if (!data) {
    return <div>No data available</div>;
  }

  const payment = data.payment || {};
  const groupType = getGroupType(data.group);
  const cashIn = isCashIn(groupType);
  const isWithdrawal = payment.object === "AffiliatorWithdrawal";
  const hasWarningStatus =
    payment.status &&
    !["Selesai", "Menunggu"].includes(payment.status);
  const hasRejectReason =
    payment.status &&
    payment.status.match(/(ditolak|reject|gagal|kedaluwarsa|dibatalkan)/i);

  return (
    <ModalTable
      role="table"
      aria-label="Balance log transaction details"
      aria-describedby="balance-log-detail-description"
    >
      <caption id="balance-log-detail-description" className="sr-only">
        Detailed information about the balance log transaction
      </caption>
      <tbody>
        <tr>
          <th scope="row">Tanggal</th>
          <td>{data.created_at || "-"}</td>
        </tr>
        <tr>
          <th scope="row">Tipe</th>
          <td>
            {data.group ? (
              <StatusBadge $groupType={groupType}>{data.group}</StatusBadge>
            ) : (
              "-"
            )}
          </td>
        </tr>
        <tr>
          <th scope="row">Nominal</th>
          <td>
            {data.price ? (
              <NominalContainer $isCashIn={cashIn}>
                {cashIn ? "+" : "-"} {data.price}
              </NominalContainer>
            ) : (
              "-"
            )}
          </td>
        </tr>
        {isWithdrawal && (
          <tr>
            <th scope="row">Status</th>
            <td>
              {payment.status ? (
                <>
                  <StatusContainer>
                    <StatusText $isWarning={hasWarningStatus}>
                      {payment.status}
                    </StatusText>
                    {hasWarningStatus && (
                      <StatusWarningIcon
                        src={unmodule(
                          require("../../images/icons/warning.svg"),
                        )}
                        alt="Warning"
                        aria-hidden="true"
                      />
                    )}
                  </StatusContainer>
                  {hasRejectReason && payment.reject_reason && (
                    <RejectReasonText>{payment.reject_reason}</RejectReasonText>
                  )}
                </>
              ) : (
                "-"
              )}
            </td>
          </tr>
        )}
        <tr>
          <th scope="row">No. Referensi</th>
          <td>{payment.number || "-"}</td>
        </tr>
        <tr>
          <th scope="row">Ref. User</th>
          <td>{payment.user_name || "-"}</td>
        </tr>
        <tr>
          <th scope="row">Desc</th>
          <td>{payment.desc || "-"}</td>
        </tr>
        {isWithdrawal && payment.payout_url && (
          <tr>
            <th scope="row">Payout Link</th>
            <td>
              <PayoutLink
                href={payment.payout_url}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Open payout link in new tab"
              >
                <img
                  src={unmodule(
                    require("../../images/icons/money-bill-transfer.svg"),
                  )}
                  alt=""
                  aria-hidden="true"
                />
                Buka Link Pembayaran
              </PayoutLink>
            </td>
          </tr>
        )}
      </tbody>
    </ModalTable>
  );
};

const UnnamedComponent = (props) => {
  const { openModal } = React.use(ModalContext);
  const {
    clearable,
    currentPage,
    dateFilterState,
    datePickerParams,
    fetchData,
    fetchable,
    fetching,
    filterCount,
    handleApplyFilter,
    handleClearFilter,
    handlePageChange,
    inputParams,
    resources,
    resourcesMeta,
  } = React.use(BalanceLogContext);
  const [filterShown, setFilterShown] = React.useState(false);
  const [dateFilterShown, setDateFilterShown] = React.useState(false);

  React.useEffect(() => {
    if (fetching) {
      setFilterShown(false);
      setDateFilterShown(false);
    }
  }, [fetching]);

  const toggleFilter = React.useCallback((evt) => {
    evt.stopPropagation();
    setFilterShown((x) => !x);
    setDateFilterShown(false);
  }, []);
  const toggleDateFilter = React.useCallback((evt) => {
    evt.stopPropagation();
    setDateFilterShown((x) => !x);
    setFilterShown(false);
  }, []);
  React.useEffect(() => {
    if (filterShown) {
      document.addEventListener("click", toggleFilter);
      return () => {
        document.removeEventListener("click", toggleFilter);
      };
    }
    if (dateFilterShown) {
      document.addEventListener("click", toggleDateFilter);
      return () => {
        document.removeEventListener("click", toggleDateFilter);
      };
    }
  }, [filterShown, dateFilterShown]);

  const showDetail = React.useCallback(
    (data) => () => {
      const paymentObject = data?.payment?.object || "Unknown";
      openModal({
        title: `Komisi - ${paymentObject}`,
        body: <BalanceLogDetailModal data={data} />,
      });
    },
    [openModal],
  );

  const handleRefresh = React.useCallback(
    (evt) => {
      evt.stopPropagation();
      if (!fetching && fetchData) {
        fetchData();
      }
    },
    [fetchData, fetching],
  );

  return (
    <Section $paddingY>
      <SectionTitle>Jurnal Kas</SectionTitle>
      <TableHeader>
        <FilterDiv onClick={toggleFilter}>
          <FilterTextSpan
            {...(filterCount > 0 && {
              $active: true,
            })}
          >
            Filter ({filterCount})
          </FilterTextSpan>
          <img src={unmodule(require("../../images/icons/angle-down.svg"))} />
          <FilterBox
            onClick={(evt) => evt.stopPropagation()}
            {...(filterShown && {
              $shown: true,
            })}
          >
            <FilterBoxHeader>
              <FilterBoxActionClearButton
                onClick={handleClearFilter}
                {...(!clearable && {
                  disabled: true,
                })}
              >
                Clear All
              </FilterBoxActionClearButton>
              <FilterBoxActionApplyButton
                onClick={handleApplyFilter}
                {...(!fetchable && {
                  disabled: true,
                })}
              >
                Apply Filter
              </FilterBoxActionApplyButton>
            </FilterBoxHeader>
            <FilterBoxHeaderFieldSeparator />
            <FilterBoxFields>
              <FilterBoxField>
                <FilterBoxTitle>STATUS</FilterBoxTitle>
                <FilterLabelContainer>
                  <FilterCheckboxLabel>
                    <FilterCheckboxInput
                      {...inputParams("group", "checkbox", "in_accepted")}
                    />
                    <div>
                      Kas Masuk
                      <span>Berhasil</span>
                    </div>
                  </FilterCheckboxLabel>
                  <FilterCheckboxLabel>
                    <FilterCheckboxInput
                      {...inputParams("group", "checkbox", "out_pending")}
                    />
                    <div>
                      Kas Keluar
                      <span>Pending</span>
                    </div>
                  </FilterCheckboxLabel>
                  <FilterCheckboxLabel>
                    <FilterCheckboxInput
                      {...inputParams("group", "checkbox", "out_accepted")}
                    />
                    <div>
                      Kas Keluar
                      <span>Berhasil</span>
                    </div>
                  </FilterCheckboxLabel>
                </FilterLabelContainer>
              </FilterBoxField>
            </FilterBoxFields>
          </FilterBox>
        </FilterDiv>
        <DateFilterDiv onClick={toggleDateFilter}>
          <img src={unmodule(require("../../images/icons/calendar.svg"))} />
          <span>
            {format(dateFilterState.dateQuery?.created_at?.[0], "dd/MM/yyyy")}
          </span>
          <span>
            {format(dateFilterState.dateQuery?.created_at?.[1], "dd/MM/yyyy")}
          </span>
          <img src={unmodule(require("../../images/icons/angle-down.svg"))} />
          <FilterBox
            onClick={(evt) => evt.stopPropagation()}
            {...(dateFilterShown && {
              $shown: true,
            })}
          >
            <FilterBoxHeader>
              <div />
              <FilterBoxActionApplyButton onClick={handleApplyFilter}>
                Apply Filter
              </FilterBoxActionApplyButton>
            </FilterBoxHeader>
            <FilterBoxHeaderFieldSeparator />
            <FilterBoxFields>
              <FilterBoxField>
                <FilterBoxTitle>TANGGAL</FilterBoxTitle>
                <FilterLabelContainer>
                  <FilterSelectLabel>
                    <span>from</span>
                    <DatePicker {...datePickerParams("created_at", 0)} />
                  </FilterSelectLabel>
                  <FilterSelectLabel>
                    <span>to</span>
                    <DatePicker {...datePickerParams("created_at", 1)} />
                  </FilterSelectLabel>
                </FilterLabelContainer>
              </FilterBoxField>
            </FilterBoxFields>
          </FilterBox>
        </DateFilterDiv>
        <RefreshButton onClick={handleRefresh} disabled={fetching}>
          <span>⟳</span>
          <span>Refresh</span>
        </RefreshButton>
      </TableHeader>
      <TableContainer>
        <DefaultTable>
          <thead>
            <tr>
              <th>#</th>
              <th>Tanggal</th>
              <th>Tipe</th>
              <th>Nominal</th>
              <th>Keterangan</th>
            </tr>
          </thead>
          <tbody>
            {resources.map((x, idx) => (
              <ClickableTr key={x.id} onClick={showDetail(x)}>
                <td>
                  {idx + 1 + (resourcesMeta.page - 1) * resourcesMeta.perpage}.
                </td>
                <td>{x.created_at}</td>
                <td>
                  <StatusBadge $groupType={getGroupType(x.group)}>
                    {x.group}
                  </StatusBadge>
                </td>
                <td>
                  <NominalContainer $isCashIn={isCashIn(getGroupType(x.group))}>
                    {isCashIn(getGroupType(x.group)) ? "+" : "-"} {x.price}
                  </NominalContainer>
                </td>
                <td>
                  <div>{x.payment.object}</div>
                </td>
              </ClickableTr>
            ))}
          </tbody>
        </DefaultTable>
      </TableContainer>
      {resourcesMeta.pages > 1 && (
        <PaginationContainer>
          <PaginationButton
            onClick={() => handlePageChange(Math.max(1, currentPage - 5))}
            disabled={currentPage <= 1}
            title="Go back 5 pages"
          >
            &lt;&lt;
          </PaginationButton>
          <PaginationButton
            onClick={() => handlePageChange(Math.max(1, currentPage - 1))}
            disabled={currentPage <= 1}
            title="Go to previous page"
          >
            &lt;
          </PaginationButton>
          {(() => {
            const totalPages = resourcesMeta.pages || 1;
            const current = currentPage || 1;
            const maxVisible = 5;
            let startPage, endPage;

            if (totalPages <= maxVisible) {
              startPage = 1;
              endPage = totalPages;
            } else if (current <= 3) {
              startPage = 1;
              endPage = maxVisible;
            } else if (current >= totalPages - 2) {
              startPage = totalPages - maxVisible + 1;
              endPage = totalPages;
            } else {
              startPage = current - 2;
              endPage = current + 2;
            }

            const pages = [];
            for (let i = startPage; i <= endPage; i++) {
              pages.push(i);
            }

            return pages.map((page) => (
              <PaginationPageButton
                key={page}
                onClick={() => handlePageChange(page)}
                $active={page === current}
              >
                {page}
              </PaginationPageButton>
            ));
          })()}
          <PaginationButton
            onClick={() =>
              handlePageChange(
                Math.min(resourcesMeta.pages || 1, currentPage + 1),
              )
            }
            disabled={currentPage >= (resourcesMeta.pages || 1)}
            title="Go to next page"
          >
            &gt;
          </PaginationButton>
          <PaginationButton
            onClick={() =>
              handlePageChange(
                Math.min(resourcesMeta.pages || 1, currentPage + 5),
              )
            }
            disabled={currentPage >= (resourcesMeta.pages || 1)}
            title="Go forward 5 pages"
          >
            &gt;&gt;
          </PaginationButton>
        </PaginationContainer>
      )}
    </Section>
  );
};
export default UnnamedComponent;

const TableContainer = styled.div`
  background-color: #2e2e33;
  border: 1px solid #484848;
  overflow-x: auto;
  padding: 15px 0 0;
`;

const DefaultTable = styled.table`
  border-collapse: collapse;
  width: 100%;

  & th {
    border-bottom: 1px solid #757575;
    padding: 20px;
    text-align: left;

    &:not(:first-child) {
    }
  }

  & td {
    padding: 20px;
  }

  & tr:not(:last-child) > td {
    border-bottom: 1px solid #595959;
  }
`;

const ClickableTr = styled.tr`
  cursor: pointer;

  &:hover {
    & > td {
      background-color: #333333;
    }
  }
`;

const TableHeader = styled.div`
  align-items: center;
  display: flex;
  gap: 10px;
  margin-left: 10px;
  margin-bottom: 20px;
`;

const FilterDiv = styled.div`
  -moz-user-select: none;
  -ms-user-select: none;
  -webkit-user-select: none;
  align-items: center;
  background-color: #393939;
  border-radius: 5px;
  border: 1px solid #484848;
  color: #cccccc;
  cursor: pointer;
  display: flex;
  flex-wrap: wrap;
  gap: 5px;
  justify-content: center;
  padding: 10px;
  position: relative;
  user-select: none;

  &:hover {
    box-shadow: 1px 1px 3px #acacac;
  }

  & > img {
    width: 18px;
  }
`;

const DateFilterDiv = styled(FilterDiv)`
  & > img:first-child {
    margin-right: 5px;
  }

  & > span {
    font-size: 14px;
  }
`;

const RefreshButton = styled.button`
  -moz-user-select: none;
  -ms-user-select: none;
  -webkit-user-select: none;
  align-items: center;
  background-color: #393939;
  border-radius: 5px;
  border: 1px solid #484848;
  color: #cccccc;
  cursor: pointer;
  display: flex;
  flex-wrap: wrap;
  gap: 5px;
  justify-content: center;
  padding: 10px;
  position: relative;
  user-select: none;
  font-size: 14px;

  &:hover:not(:disabled) {
    box-shadow: 1px 1px 3px #acacac;
  }

  & > span:first-child {
    font-size: 18px;
    display: inline-block;
    transition: transform 0.3s ease;
  }

  &:disabled {
    cursor: not-allowed;
    filter: brightness(0.5);
    opacity: 0.6;
  }

  &:not(:disabled):active > span:first-child {
    transform: rotate(360deg);
  }
`;

const FilterBox = styled.div`
  background-color: #3e3e3e;
  border-radius: 5px;
  border: 1px solid #646464;
  color: white;
  cursor: default;
  display: none;
  left: 0;
  min-width: 220px;
  position: absolute;
  top: 36px;

  ${(props) =>
    props.$shown &&
    css`
      display: block;
    `};
`;

const FilterBoxTitle = styled.div`
  font-weight: bold;
  margin: 10px 0 20px;
`;

const FilterBoxHeader = styled.div`
  align-items: center;
  display: flex;
  justify-content: space-between;
  margin: 15px;
`;

const cssFilterBoxActionButton = css`
  border-radius: 3px;
  cursor: pointer;
  padding: 6px 7px;
  transition: background-color 0.2s ease;

  &[disabled] {
    cursor: not-allowed;
    filter: brightness(0.5);
    background-color: transparent;

    &:hover {
      background-color: transparent;
    }
  }
`;

const FilterBoxActionClearButton = styled.button`
  ${cssFilterBoxActionButton};
  background-color: transparent;
  border: 1px solid #ff9800;
  color: #ff9800;

  &:hover {
    background-color: #ff980040;
  }
`;

const FilterBoxActionApplyButton = styled.button`
  ${cssFilterBoxActionButton};
  background-color: #824e00;
  border: 1px solid #ff9800;
  color: #ffffff;

  &:hover {
    background-color: #b26c02;
  }
`;

const FilterBoxHeaderFieldSeparator = styled.div`
  background-color: #646464;
  height: 1px;
`;

const FilterBoxFields = styled.div`
  display: flex;

  @media ${narrowScreenMediaQuery} {
    flex-wrap: wrap;
  }
`;

const FilterBoxField = styled.div`
  padding: 15px;
  border-right: 1px solid #646464;
  flex: 1 1 0;
`;

const FilterLabelContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const FilterCheckboxLabel = styled.label`
  align-items: center;
  cursor: pointer;
  display: flex;
  font-size: 14px;
  gap: 10px;
  padding: 5px 20px 5px 5px;
  white-space: nowrap;
  transition: background-color 0.3s;

  &:hover {
    background-color: #f4433621;
    color: #ff9800;
  }
`;

const FilterCheckboxInput = styled.input`
  accent-color: #ff9800;
  height: 20px;
  width: 20px;
`;

const FilterSelectLabel = styled.div`
  color: black;
  margin-bottom: 10px;
  min-width: 250px;

  & > span {
    color: white;
    display: block;
    margin-bottom: 5px;
  }

  & input {
    border-radius: 4px;
    border: 1px solid #aeaeae;
    box-shadow: inset 0 2px 2px #e9e9e9;
    font-size: 16px;
    letter-spacing: 0.5px;
    line-height: 16px;
    padding: 6px 10px 5px;
  }
`;

const FilterTextSpan = styled.span`
  ${(props) =>
    props.$active &&
    css`
      color: ${orangish};
    `};
`;

const PayoutLink = styled.a`
  align-items: center;
  color: #ff9800;
  display: inline-flex;
  gap: 8px;
  text-decoration: none;
  word-break: break-all;

  &:hover {
    color: #ffb74d;
    text-decoration: underline;
  }

  & > img {
    height: 16px;
    width: 16px;
  }
`;

const StatusContainer = styled.div`
  align-items: center;
  display: inline-flex;
  gap: 8px;
`;

const StatusText = styled.span`
  ${(props) =>
    props.$isWarning &&
    css`
      color: #f44336;
      font-weight: bold;
    `};
`;

const StatusWarningIcon = styled.img`
  height: 16px;
  width: 16px;
`;

const RejectReasonText = styled.div`
  color: #f44336;
  font-size: 13px;
  margin-top: 8px;
  padding: 8px;
  background-color: #f4433615;
  border-left: 3px solid #f44336;
  border-radius: 3px;
`;

const ModalTable = styled.table`
  border-collapse: collapse;
  width: 100%;

  & caption {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border-width: 0;
  }

  & th,
  & td {
    padding: 14px 18px;
    vertical-align: top;
    text-align: left;
  }

  & th {
    color: #9f9f9f;
    font-weight: 500;
    font-size: 14px;
    min-width: 140px;
    white-space: nowrap;
    letter-spacing: 0.3px;
  }

  & td {
    color: #ffffff;
    font-size: 14px;
    word-break: break-word;
  }

  & tr:not(:last-child) > th,
  & tr:not(:last-child) > td {
    border-bottom: 1px solid #484848;
  }
`;

const NominalContainer = styled.span`
  align-items: center;
  display: inline-flex;
  font-weight: 500;
  gap: 4px;

  ${(props) =>
    props.$isCashIn
      ? css`
          color: #4caf50;
        `
      : css`
          color: #f44336;
        `}
`;

const StatusBadge = styled.span`
  align-items: center;
  border-radius: 5px;
  display: inline-flex;
  font-size: 12px;
  font-weight: 500;
  justify-content: center;
  padding: 4px 10px;
  white-space: nowrap;

  ${(props) => {
    switch (props.$groupType) {
      case "in_accepted":
        return css`
          background-color: #009688;
          color: #ffffff;
        `;
      case "out_accepted":
        return css`
          background-color: #2196f3;
          color: #ffffff;
        `;
      case "in_pending":
      case "out_pending":
        return css`
          background-color: #fff3e0;
          color: #e65100;
        `;
      case "in_refunded":
        return css`
          background-color: #ffebee;
          color: #c62828;
        `;
      default:
        return css`
          background-color: #757575;
          color: #ffffff;
        `;
    }
  }}
`;

const PaginationContainer = styled.div`
  align-items: center;
  display: flex;
  gap: 5px;
  justify-content: center;
  margin-top: 20px;
  padding: 10px;
`;

const PaginationButton = styled.button`
  align-items: center;
  background-color: #393939;
  border-radius: 5px;
  border: 1px solid #484848;
  color: #cccccc;
  cursor: pointer;
  display: flex;
  font-size: 14px;
  justify-content: center;
  min-width: 36px;
  padding: 8px 12px;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    background-color: #484848;
    box-shadow: 1px 1px 3px #acacac;
  }

  &:disabled {
    cursor: not-allowed;
    filter: brightness(0.5);
    opacity: 0.5;
  }
`;

const PaginationPageButton = styled.button`
  align-items: center;
  background-color: ${(props) =>
    props.$active ? "#824e00" : "#393939"};
  border-radius: 5px;
  border: 1px solid ${(props) => (props.$active ? "#ff9800" : "#484848")};
  color: ${(props) => (props.$active ? "#ffffff" : "#cccccc")};
  cursor: pointer;
  display: flex;
  font-size: 14px;
  font-weight: ${(props) => (props.$active ? "bold" : "normal")};
  justify-content: center;
  min-width: 36px;
  padding: 8px 12px;
  transition: all 0.2s ease;

  &:hover {
    background-color: ${(props) =>
      props.$active ? "#b26c02" : "#484848"};
    box-shadow: 1px 1px 3px #acacac;
  }
`;
